import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, ShoppingBag, Zap, Truck, RotateCcw, ShieldCheck, Wind, Battery, Gauge, Feather, Star } from "lucide-react";
import { PRODUCT_COLORS, PRODUCT_IMAGES, PRICE, COMPARE_PRICE, DISCOUNT_PERCENT } from "@/lib/product-data";

function StarRating({ count }) {
  return (
    <span className="inline-flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={14} fill={i < count ? "#22D3EE" : "transparent"} className={i < count ? "text-cyan-400" : "text-slate-600"} />
      ))}
    </span>
  );
}

export { StarRating };

export default function HeroSection({ selectedColor, setSelectedColor, qty, setQty, onAddToCart, addedToCart }) {
  const [currentImage, setCurrentImage] = useState(0);
  const color = PRODUCT_COLORS[selectedColor];

  const FEATURES = [
    { Icon: Wind, title: "Bladeless & Safe", desc: "No exposed blades — safe around kids and pets." },
    { Icon: Battery, title: "All-Day Battery", desc: "1500mAh delivers 2-6 hours of cooling." },
    { Icon: Gauge, title: "5 Speed Settings", desc: "From whisper to turbo — find your flow." },
    { Icon: Feather, title: "Featherweight 400g", desc: "So light you'll forget it's there." },
  ];

  const TRUST = [
    { Icon: Truck, text: "Free Shipping" },
    { Icon: RotateCcw, text: "30-Day Returns" },
    { Icon: Zap, text: "Ships in 24h" },
  ];

  return (
    <section data-testid="hero-section" className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-24">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-start">
        {/* Gallery */}
        <motion.div className="md:sticky md:top-28" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <div
            data-testid="product-image-gallery"
            className="relative aspect-square rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/10 overflow-hidden"
          >
            <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-cyan-400 to-blue-500 text-[#050B14] px-3 py-1 rounded-full text-xs font-bold tracking-wide">
              -{DISCOUNT_PERCENT}% OFF
            </div>
            {currentImage === 0 && color.id !== "white" && (
              <div className="absolute inset-0 z-[1] pointer-events-none transition-colors duration-500" style={{ backgroundColor: color.hex, opacity: 0.06 }} />
            )}
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImage}
                src={PRODUCT_IMAGES[currentImage].url}
                alt={PRODUCT_IMAGES[currentImage].alt}
                className="w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.3 }}
              />
            </AnimatePresence>
            {currentImage === 0 && (
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-black/50 backdrop-blur-md text-xs font-bold uppercase tracking-[0.15em] text-slate-300 px-4 py-1.5 rounded-full border border-white/10">
                {color.name}
              </span>
            )}
          </div>
          <div className="flex gap-3 mt-4">
            {PRODUCT_IMAGES.map((img, i) => (
              <button
                key={i}
                data-testid={`image-thumb-${i}`}
                onClick={() => setCurrentImage(i)}
                className={`relative flex-1 aspect-square rounded-xl border overflow-hidden transition-all duration-200 ${
                  currentImage === i
                    ? "border-cyan-400/60 shadow-[0_0_15px_rgba(34,211,238,0.15)]"
                    : "border-white/10 opacity-50 hover:opacity-80"
                }`}
              >
                <img src={img.thumb} alt={img.label} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </motion.div>

        {/* Info */}
        <motion.div className="space-y-6" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
          <div className="flex items-center gap-3" data-testid="product-rating">
            <StarRating count={5} />
            <span className="text-sm text-slate-400">4.8 · 2,341 reviews</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.92]">
            Bladeless<br />Neck Fan
          </h1>

          <p className="text-base text-slate-400 leading-relaxed max-w-md">
            360° hands-free cooling with zero blades, zero noise, zero hassle. Rechargeable, portable, and built for your life.
          </p>

          <div className="flex items-baseline gap-3 flex-wrap" data-testid="product-price">
            <span className="text-4xl font-bold">${PRICE.toFixed(2)}</span>
            <span className="text-lg text-slate-500 line-through">${COMPARE_PRICE.toFixed(2)}</span>
            <span className="text-sm font-bold text-cyan-400 bg-cyan-400/10 px-3 py-1 rounded-full">
              Save ${(COMPARE_PRICE - PRICE).toFixed(2)}
            </span>
          </div>

          {/* Color Selector */}
          <div data-testid="color-selector">
            <span className="text-xs font-bold tracking-[0.15em] uppercase block mb-3 text-slate-400">
              Color — <span className="text-white normal-case tracking-normal">{color.name}</span>
            </span>
            <div className="flex gap-3">
              {PRODUCT_COLORS.map((c, i) => (
                <button
                  key={c.id}
                  data-testid={`color-selector-${c.id}`}
                  onClick={() => { setSelectedColor(i); setCurrentImage(0); }}
                  className={`w-10 h-10 rounded-full transition-all duration-200 ${
                    selectedColor === i
                      ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-[#050B14] scale-110"
                      : "opacity-50 hover:opacity-100 hover:scale-110"
                  }`}
                  style={{ backgroundColor: c.hex }}
                  aria-label={`Select ${c.name}`}
                />
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div data-testid="quantity-selector">
            <span className="text-xs font-bold tracking-[0.15em] uppercase block mb-3 text-slate-400">Quantity</span>
            <div className="inline-flex items-center bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden backdrop-blur-md">
              <button data-testid="quantity-decrease-btn" onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 flex items-center justify-center hover:bg-white/5 transition-colors text-slate-300">
                <Minus size={18} />
              </button>
              <span className="w-14 h-12 flex items-center justify-center font-bold text-lg border-x border-white/10">{qty}</span>
              <button data-testid="quantity-increase-btn" onClick={() => setQty(Math.min(10, qty + 1))} className="w-12 h-12 flex items-center justify-center hover:bg-white/5 transition-colors text-slate-300">
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-3 pt-2">
            <motion.button
              data-testid="add-to-cart-button"
              onClick={onAddToCart}
              whileTap={{ scale: 0.97 }}
              className={`w-full py-4 px-8 text-base font-bold rounded-full transition-all duration-300 flex items-center justify-center gap-2.5 ${
                addedToCart
                  ? "bg-emerald-500 text-white shadow-[0_0_30px_rgba(16,185,129,0.3)]"
                  : "bg-gradient-to-r from-cyan-400 to-blue-500 text-[#050B14] shadow-[0_0_30px_rgba(34,211,238,0.3)] hover:shadow-[0_0_50px_rgba(34,211,238,0.5)] hover:scale-[1.02]"
              }`}
            >
              {addedToCart ? (<><ShieldCheck size={20} /> Added to Cart</>) : (<><ShoppingBag size={20} /> Add to Cart — ${(PRICE * qty).toFixed(2)}</>)}
            </motion.button>
            <button
              data-testid="express-checkout-button"
              className="w-full py-4 px-8 text-base font-bold rounded-full bg-white/5 border border-white/10 text-white backdrop-blur-md hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Zap size={18} /> Express Checkout
            </button>
          </div>

          {/* Trust */}
          <div className="flex flex-wrap gap-2.5 pt-2" data-testid="trust-badges">
            {TRUST.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-full px-4 py-2 text-xs font-semibold text-slate-300 backdrop-blur-md">
                <b.Icon size={14} className="text-cyan-400" /> {b.text}
              </span>
            ))}
          </div>

          {/* Features */}
          <div className="border-t border-white/10 pt-8 mt-4 space-y-5" data-testid="feature-highlights">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 block">Why You'll Love It</span>
            {FEATURES.map((f, i) => (
              <div key={i} className="flex gap-4 items-start group">
                <div className="w-10 h-10 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-400/10 group-hover:border-cyan-400/20 transition-colors">
                  <f.Icon size={18} className="text-cyan-400" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{f.title}</div>
                  <div className="text-sm text-slate-400">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
